const ensureString = (valor) => (valor === null || valor === undefined ? "" : String(valor));

const normalizarNfe = (valor) => {
  const texto = ensureString(valor).toUpperCase();
  const somenteNumeros = texto.replace(/[^0-9]/g, "");
  return somenteNumeros || texto.trim();
};

const normalizarChassi = (valor) =>
  ensureString(valor)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

const normalizarArquivo = (valor) => ensureString(valor).trim();

const normalizarIndice = (valor) => {
  const numero = Number(valor);
  return Number.isFinite(numero) && Number.isInteger(numero) ? numero : null;
};

const removerExtensaoArquivo = (valor) => {
  const texto = ensureString(valor).trim();
  if (!texto.includes(".")) {
    return texto;
  }
  return texto.substring(0, texto.lastIndexOf("."));
};

const coletarPossiveisArquivos = (valor) => {
  const base = normalizarArquivo(valor);
  if (!base) {
    return [];
  }
  const maiusculo = base.toUpperCase();
  const semExtensao = removerExtensaoArquivo(maiusculo);
  return semExtensao && semExtensao !== maiusculo ? [maiusculo, semExtensao] : [maiusculo];
};

const extrairListasPossiveis = (origem) => {
  if (!origem) {
    return [];
  }
  if (Array.isArray(origem)) {
    return origem;
  }
  const candidatos = [
    origem.items,
    origem.itens,
    origem.jobItems,
    origem.job_items,
    origem.job?.items,
    origem.job?.itens,
    origem.job?.jobItems,
    origem.job?.job_items,
    origem.dados?.items,
    origem.dados?.itens,
    origem.data?.items,
    origem.data?.itens,
    origem.resultado?.items,
    origem.resultado?.itens,
    origem.result?.items,
    origem.result?.itens,
    origem.payload?.items,
    origem.payload?.itens,
    origem.response?.items,
    origem.response?.itens,
    origem.resposta?.items,
    origem.resposta?.itens,
  ];
  return candidatos.reduce((lista, atual) => {
    if (Array.isArray(atual)) {
      return lista.concat(atual);
    }
    return lista;
  }, []);
};

const normalizarJobItemInfo = (item, fallbackIndex = 0) => {
  const jobItemKey = ensureString(
    item?.jobItemKey ||
      item?.job_item_key ||
      item?.jobItemId ||
      item?.job_item_id ||
      item?.itemKey ||
      item?.key ||
      item?.id ||
      item?._id,
  ).trim();
  const clientId = ensureString(
    item?.clientId ||
      item?.client_id ||
      item?.clientKey ||
      item?.clienteId ||
      item?.cliente_id ||
      item?.entryId ||
      item?.entry_id ||
      item?.payloadId ||
      item?.payload_id,
  ).trim();
  const veiculoKey = ensureString(
    item?.ve101_key_public ||
      item?.veiculoKey ||
      item?.veiculo_id ||
      item?.veiculoKeyPublic,
  ).trim();
  const nfe = normalizarNfe(item?.nfe ?? item?.nf ?? item?.notaFiscal ?? item?.ve101_key_nfe);
  const chassi = normalizarChassi(item?.chassi ?? item?.chassis ?? item?.ve101_dsc_chassi);
  const arquivo = normalizarArquivo(
    item?.arquivo ?? item?.fileName ?? item?.filename ?? item?.nome ?? item?.name,
  );
  const arquivosComparacao = coletarPossiveisArquivos(arquivo);
  const index = Number.isInteger(item?.index)
    ? item.index
    : Number.isInteger(item?.posicao)
    ? item.posicao
    : Number.isInteger(item?.position)
    ? item.position
    : fallbackIndex;

  return {
    jobItemKey,
    clientId,
    veiculoKey,
    nfe,
    chassi,
    arquivosComparacao,
    arquivoOriginal: arquivo,
    raw: item,
    index,
  };
};

const associarJobItemsAosEnvios = (envios, payloadJob) => {
  if (!Array.isArray(envios) || envios.length === 0) {
    return envios;
  }
  const listaBruta = Array.isArray(payloadJob) ? payloadJob : extrairListasPossiveis(payloadJob);
  if (!Array.isArray(listaBruta) || listaBruta.length === 0) {
    return envios;
  }

  const infosJob = listaBruta.map((item, idx) => normalizarJobItemInfo(item, idx));
  const resultado = envios.map((item) => ({ ...item }));
  const ocupados = new Set();

  const procurar = (comparador) => {
    for (let i = 0; i < resultado.length; i += 1) {
      if (ocupados.has(i)) {
        continue;
      }
      const candidato = resultado[i];
      if (comparador(candidato, i)) {
        ocupados.add(i);
        return { candidato, indice: i };
      }
    }
    return null;
  };

  infosJob.forEach((info) => {
    const match =
      procurar((entrada) => info.clientId && ensureString(entrada.clientId || entrada.id).trim() === info.clientId) ||
      procurar((entrada) => info.jobItemKey && entrada.jobItemKey === info.jobItemKey) ||
      procurar((entrada) => info.veiculoKey && entrada.ve101_key_public && entrada.ve101_key_public === info.veiculoKey) ||
      procurar((entrada) => info.nfe && info.chassi && entrada.nfe === info.nfe && entrada.chassi === info.chassi) ||
      procurar((entrada) => info.nfe && entrada.nfe === info.nfe) ||
      procurar((entrada) => info.chassi && entrada.chassi === info.chassi) ||
      procurar((entrada) => {
        if (!info.arquivosComparacao.length) {
          return false;
        }
        const arquivoEntrada = normalizarArquivo(entrada.arquivo).toUpperCase();
        if (!arquivoEntrada) {
          return false;
        }
        const arquivoSemExt = removerExtensaoArquivo(arquivoEntrada);
        return info.arquivosComparacao.includes(arquivoEntrada) || (arquivoSemExt && info.arquivosComparacao.includes(arquivoSemExt));
      }) ||
      procurar(() => true);

    if (match) {
      resultado[match.indice] = {
        ...match.candidato,
        jobItemKey: info.jobItemKey || match.candidato.jobItemKey,
        jobItemIndex: Number.isInteger(info.index) ? info.index : match.candidato.jobItemIndex,
        backendArquivo: info.arquivoOriginal || match.candidato.backendArquivo,
        backendReferencia: info.raw || match.candidato.backendReferencia,
        clientId: match.candidato.clientId || info.clientId || match.candidato.id,
        ve101_key_public: match.candidato.ve101_key_public || info.veiculoKey || match.candidato.ve101_key_public,
      };
    }
  });

  return resultado;
};

export {
  ensureString,
  normalizarNfe,
  normalizarChassi,
  normalizarArquivo,
  normalizarIndice,
  removerExtensaoArquivo,
  coletarPossiveisArquivos,
  extrairListasPossiveis,
  normalizarJobItemInfo,
  associarJobItemsAosEnvios,
};
